import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { ConfirmDialog } from 'components/ui/confirm-dialog';
import { QUESTION_TYPE_META } from 'pages/QuestionBank/questionTypeMeta';
import { ExamQuestionDialog, type ExamQuestionPayload } from './ExamQuestionDialog';
import type { IeltsWritingTask, SectionKind } from '../../types';

interface Props {
  kind: SectionKind;
  questions: Array<Record<string, any> & { id: string; type: string }>;
  uploadContext: string;
  disabled?: boolean;
  onAdd: (payload: ExamQuestionPayload) => Promise<void>;
  onUpdate: (questionId: string, payload: ExamQuestionPayload) => Promise<void>;
  onDelete: (questionId: string) => Promise<void>;
  fixedWritingTask?: IeltsWritingTask;
}

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

/** Questions of one exam section (or library part), with add / edit / delete. */
export function ExamQuestionList({ kind, questions, uploadContext, disabled, onAdd, onUpdate, onDelete, fixedWritingTask }: Props) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<Record<string, any> | null>(null);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const itemCount = (q: Record<string, any>) => (q.type === 'PASSAGE' ? q.subQuestions?.length ?? 0 : 1);

  return (
    <div className="space-y-2">
      {questions.length ? (
        <ol className="divide-y divide-border rounded-lg border border-border">
          {questions.map((q, i) => {
            const label = stripHtml(q.type === 'PASSAGE' ? q.passageTitle || q.passageText || q.prompt || '' : q.prompt || '');
            return (
              <li key={q.id} className="flex items-center gap-3 px-3 py-2.5">
                <span className="w-6 shrink-0 text-right text-xs font-semibold text-muted-foreground">{i + 1}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-foreground">{label || t('exam.untitled_question')}</span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {t(QUESTION_TYPE_META[q.type]?.labelKey || q.type)}
                    {q.type === 'PASSAGE' && ` · ${t('exam.library.n_questions', { count: itemCount(q) })}`}
                    {q.writingTask && ` · ${q.writingTask === 'TASK_1' ? t('exam.writing.task1') : t('exam.writing.task2')}`}
                    {q.domain && ` · ${q.domain}`}
                  </span>
                </span>
                <Button size="icon" variant="ghost" className="h-8 w-8" disabled={disabled} onClick={() => setEditing(q)} aria-label={t('edit_question')}>
                  <Pencil className="h-4 w-4" aria-hidden />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 text-destructive"
                  disabled={disabled}
                  onClick={() => setDeleting(q.id)}
                  aria-label={t('delete')}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="rounded-lg border border-dashed border-border px-3 py-4 text-center text-sm text-muted-foreground">{t('exam.no_questions_yet')}</p>
      )}
      <Button size="sm" variant="outline" disabled={disabled} onClick={() => setAdding(true)} className="gap-1.5">
        <Plus className="h-4 w-4" aria-hidden /> {t('add_question')}
      </Button>

      <ExamQuestionDialog
        open={adding || Boolean(editing)}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        kind={kind}
        question={editing}
        uploadContext={uploadContext}
        fixedWritingTask={fixedWritingTask}
        onSave={(payload) => (editing ? onUpdate(editing.id, payload) : onAdd(payload))}
      />
      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t('exam.delete_question_title')}
        description={t('exam.delete_question_body')}
        confirmLabel={t('delete')}
        cancelLabel={t('cancel')}
        destructive
        loading={busy}
        onConfirm={async () => {
          if (!deleting) return;
          setBusy(true);
          try {
            await onDelete(deleting);
            setDeleting(null);
          } finally {
            setBusy(false);
          }
        }}
      />
    </div>
  );
}

export default ExamQuestionList;
